import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 1000;

export class VariantCartTable extends Component {
  constructor() {
    super();
    this.cart = {};
    this.isUpdating = false;
    this.isExpanded = false;
  }

  connectedCallback() {
    this.fetchCart();
    this.attachEventListeners();
    this.initializeLoadMore();
  }

  attachEventListeners() {
    // Handle increase/decrease button clicks
    this.querySelectorAll(".qty-btn").forEach((btn) => {
      btn.addEventListener("click", (e) => this.handleQuantityChange(e));
    });

    // Handle manual input changes
    this.querySelectorAll(".qty-input").forEach((input) => {
      input.addEventListener("change", (e) => this.handleManualInput(e));
    });
  }

  initializeLoadMore() {
    const loadMoreBtn = this.querySelector(".load-more-btn");
    if (loadMoreBtn) {
      loadMoreBtn.addEventListener("click", () => this.toggleVariants());
    }

    // Handle window resize to show/hide variants appropriately
    this.handleResize();
    window.addEventListener("resize", () => this.handleResize());
  }

  handleResize() {
    const isMobile = window.innerWidth < 768;
    const allRows = this.querySelectorAll(".variant-row");
    const totalVariants = parseInt(this.dataset.totalVariants) || 0;

    if (!isMobile) {
      // On desktop, show all variants
      allRows.forEach((row) => {
        row.classList.remove("hidden");
      });
      this.isExpanded = false;
    } else {
      // On mobile, respect the expanded state
      if (!this.isExpanded && totalVariants >= 8) {
        allRows.forEach((row, index) => {
          if (index >= 8) {
            row.classList.add("hidden");
          }
        });
      }
    }
  }

  toggleVariants() {
    const hiddenRows = this.querySelectorAll(".variant-row.hidden");
    const loadMoreBtn = this.querySelector(".load-more-btn");
    const loadMoreText = loadMoreBtn.querySelector(".load-more-text");
    const totalVariants = parseInt(this.dataset.totalVariants) || 0;
    const initialVisible = 8;

    // Get show less text from the section settings or use default
    const showLessText = "Show Less Sizes";
    const showMoreText = "Show More";
    const moreText = "Sizes";
    const remainingCount = loadMoreBtn.dataset.remaining;

    this.isExpanded = !this.isExpanded;

    if (this.isExpanded) {
      // Show all hidden rows
      hiddenRows.forEach((row) => {
        row.classList.remove("hidden");
      });
      loadMoreText.textContent = showLessText;
      loadMoreBtn.classList.add("expanded");
    } else {
      // Hide rows again
      const allRows = this.querySelectorAll(".variant-row");

      allRows.forEach((row, index) => {
        if (index >= initialVisible) {
          row.classList.add("hidden");
        }
      });

      loadMoreText.textContent = `${showMoreText} ${moreText}`;
      loadMoreBtn.classList.remove("expanded");

      // Scroll to table top smoothly
      this.querySelector(".variant-table").scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  }

  async fetchCart() {
    try {
      const response = await fetch("/cart.js");
      this.cart = await response.json();
      this.updateQuantityInputs();
    } catch (error) {
      console.error("Error fetching cart:", error);
    }
  }

  updateQuantityInputs() {
    // Update all quantity inputs based on current cart state
    this.querySelectorAll(".qty-input").forEach((input) => {
      const variantId = input.dataset.variantId;
      const cartItem = this.cart.items?.find((item) => item.variant_id == variantId);
      input.value = cartItem ? cartItem.quantity : 0;
    });
  }

  async handleQuantityChange(e) {
    if (this.isUpdating) return;

    const button = e.currentTarget;
    const variantId = button.dataset.variantId;
    const action = button.dataset.action;
    const input = this.querySelector(`.qty-input[data-variant-id="${variantId}"]`);

    let currentQty = parseInt(input.value) || 0;
    let newQty = action === "increase" ? currentQty + 1 : Math.max(0, currentQty - 1);
    await this.updateCart(variantId, newQty, input, button);
  }

  async handleManualInput(e) {
    if (this.isUpdating) return;

    const input = e.target;
    const variantId = input.dataset.variantId;
    let newQty = Math.max(0, parseInt(input.value) || 0);

    await this.updateCart(variantId, newQty, input);
  }

  // updateCartBubbleQuantity(cartItemCount) {
  //   const cartBubbles = document.querySelectorAll("[ref='cartBubbleCount']");
  //   cartBubbles.forEach((cartBubble) => {
  //     cartBubble.textContent = cartItemCount;
  //   });
  // }

  updateSideCartDrawer(cart, variantId) {
    //call theme event to update the side cart drawer
    this.dispatchEvent(
      new CartAddEvent({}, this.id, {
        didError: false,
        source: "product-form-component",
        itemCount: 1,
        productId: variantId,
      })
    );
  }

  async updateCart(variantId, quantity, input, button = null) {
    this.isUpdating = true;

    const row = this.querySelector(`tr[data-variant-id="${variantId}"]`);
    const buttons = row?.querySelectorAll(".qty-btn");

    // Disable buttons during update
    buttons?.forEach((btn) => (btn.disabled = true));

    try {
      // Use the /cart/update.js endpoint
      const response = await fetch("/cart/update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          updates: {
            [variantId]: quantity,
          },
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to update cart");
      }

      this.cart = await response.json();
      //this.updateCartBubbleQuantity(this.cart?.item_count);
      this.updateSideCartDrawer(this.cart, variantId);

      // Update the input value
      input.value = quantity;

      // Dispatch cart update event for theme integration
      document.documentElement.dispatchEvent(
        new CustomEvent("cart:updated", {
          bubbles: true,
          detail: { cart: this.cart },
        })
      );
    } catch (error) {
      console.error("Error updating cart:", error);

      alert("There was an error updating your cart. Please try again.");

      // Revert to previous value
      await this.fetchCart();
    } finally {
      this.isUpdating = false;

      // Re-enable buttons
      buttons?.forEach((btn) => (btn.disabled = false));
    }
  }
}

if (!customElements.get("variant-cart-table")) {
  customElements.define("variant-cart-table", VariantCartTable);
}
